import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  appointmentsData,
  getPatientById,
  getAppointmentsByDate
} from "@/data/mockData";
import {
  Clock,
  User,
  Phone,
  MapPin,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Play,
  Calendar as CalendarIcon,
  Plus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format, addDays, startOfWeek, addWeeks, isSameDay, parseISO } from "date-fns";
import { it } from "date-fns/locale";

export const CalendarPage = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date("2026-01-27"));
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [view, setView] = useState("week"); // week or month

  // Get appointments for selected date
  const dateString = format(selectedDate, "yyyy-MM-dd");
  const dayAppointments = getAppointmentsByDate(dateString);

  // Get week dates
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Get dates with appointments for calendar highlighting
  const datesWithAppointments = useMemo(() => {
    return appointmentsData.map(a => parseISO(a.date));
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-success text-success-foreground";
      case "in-progress": return "bg-warning text-warning-foreground";
      default: return "bg-primary text-primary-foreground";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-success/10 text-success border-success/20 gap-1"><CheckCircle2 className="h-3 w-3" />Completato</Badge>;
      case "in-progress":
        return <Badge className="bg-warning/10 text-warning border-warning/20 gap-1"><Play className="h-3 w-3" />In corso</Badge>;
      default:
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Programmato</Badge>;
    }
  };

  const AppointmentCard = ({ appointment, onClick }) => {
    const patient = getPatientById(appointment.patientId);
    return (
      <div
        className={cn(
          "p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md",
          appointment.status === "completed" && "bg-success/5 border-success/30",
          appointment.status === "in-progress" && "bg-warning/5 border-warning/30",
          appointment.status === "scheduled" && "bg-card hover:border-primary/50"
        )}
        onClick={() => onClick(appointment)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={cn("w-2 h-2 rounded-full", getStatusColor(appointment.status))} />
            <span className="font-semibold text-sm">{appointment.time}</span>
          </div>
          <Badge variant="outline" className="text-xs">{appointment.duration} min</Badge>
        </div>
        <p className="font-medium">{patient?.name}</p>
        <p className="text-sm text-muted-foreground truncate">
          {appointment.services.join(", ")}
        </p>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Calendario</h1>
          <p className="text-muted-foreground">Gestisci le prenotazioni</p>
        </div>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Nuova Prenotazione
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Calendar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Seleziona Data</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={it}
              modifiers={{
                hasAppointment: datesWithAppointments
              }}
              modifiersStyles={{
                hasAppointment: {
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  textDecorationColor: 'hsl(var(--primary))',
                  textUnderlineOffset: '3px'
                }
              }}
              className="rounded-md"
            />
            
            {/* Legend */}
            <div className="mt-4 pt-4 border-t space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Legenda</p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  <span className="text-xs">Programmato</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-warning" />
                  <span className="text-xs">In corso</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-success" />
                  <span className="text-xs">Completato</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Day View */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="capitalize">
                  {format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}
                </CardTitle>
                <CardDescription>
                  {dayAppointments.length} appuntamenti
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSelectedDate(addDays(selectedDate, -1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDate(new Date("2026-01-27"))}
                >
                  Oggi
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px] pr-4">
              {dayAppointments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <CalendarIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nessun appuntamento per questa data</p>
                  <Button variant="link" className="mt-2">
                    Aggiungi prenotazione
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {dayAppointments.map((apt) => (
                    <AppointmentCard
                      key={apt.id}
                      appointment={apt}
                      onClick={setSelectedAppointment}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Week Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Panoramica Settimanale</CardTitle>
          <CardDescription>
            Settimana del {format(weekStart, "d MMMM", { locale: it })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {weekDates.map((date) => {
              const dateStr = format(date, "yyyy-MM-dd");
              const apts = getAppointmentsByDate(dateStr);
              const isSelected = isSameDay(date, selectedDate);
              const isToday = format(date, "yyyy-MM-dd") === "2026-01-27";
              
              return (
                <div
                  key={dateStr}
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-all text-center",
                    isSelected && "border-primary bg-primary/5",
                    isToday && !isSelected && "border-info bg-info/5",
                    !isSelected && !isToday && "hover:border-muted-foreground/30"
                  )}
                  onClick={() => setSelectedDate(date)}
                >
                  <p className="text-xs text-muted-foreground capitalize">
                    {format(date, "EEE", { locale: it })}
                  </p>
                  <p className={cn(
                    "text-lg font-semibold",
                    isToday && "text-info"
                  )}>
                    {format(date, "d")}
                  </p>
                  {apts.length > 0 && (
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {apts.length}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Appointment Detail Dialog */}
      <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
        <DialogContent>
          {selectedAppointment && (() => {
            const patient = getPatientById(selectedAppointment.patientId);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {selectedAppointment.time} - {patient?.name}
                  </DialogTitle>
                  <DialogDescription>
                    Dettagli appuntamento
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Stato</span>
                    {getStatusBadge(selectedAppointment.status)}
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Prestazioni</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedAppointment.services.map((s, i) => (
                        <Badge key={i} variant="outline">{s}</Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Paziente</p>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{patient?.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{patient?.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{patient?.address}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Durata stimata: {selectedAppointment.duration} minuti</span>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => navigate(`/dashboard/patients/${selectedAppointment.patientId}`)}
                    >
                      Vedi Paziente
                    </Button>
                    <Button className="flex-1">
                      Avvia Sessione
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
